// @flow

// Types
import type { Asset } from 'fc-model-assets/types'

// Model Properties
import { PROP_LABEL } from 'fc-model-assets'

// Libs
import React, { PureComponent } from 'react'
import _isEmpty from 'lodash/isEmpty'
import _find from 'lodash/find'
import _size from 'lodash/size'

// Components
import PanelAssetsForm from './PanelAssetsForm'
import FeaturesOfField from './FeaturesOfField'
import Button from '@react-mdc/button'
import Textfield from '@react-mdc/textfield'
import Formfield from '@react-mdc/form-field'

import { UploadFieldBorders } from 'fc-upload-borders'

// Styles
import Mask from 'fc-masks'
import { dark, primary, white, orange } from 'hfty-styles/src/colors'

// Props
export type Props = {
  fields: Array<Asset>,
  assets: Array,
  selectedSeasonId: ?number,
  selectedFarmId: ?number,
  selectedFieldId: ?number,
  seasonSelectorOptions: Array<{ id: number, value: string }>,
  farmSelectorOptions: Array<{ id: number, value: string }>,
  fieldSelectorOptions: Array<{ id: number, value: string }>,
  featuresOfSelectedField: Array<Object>,
  selectedFeatureIndex: ?number,
  hoverFeatureIndex: ?number,
  featureOperation: ?Object,
  uploadOperations: Object,
  isEditing: boolean,
  isActiveToolsOp: boolean,
  area: ?number,
  jobsOfSelectedField: Object,
  actions: {
    fitMapBounds: Function,
    onChangeSeason: Function,
    onChangeFarm: Function,
    onChangeField: Function,
    onSelectFeature: Function,
    onToggleHoverFeature: Function,
    // onAddFeature: Function,
    onEditSelectedField: Function,
    onDeleteSelectedField: Function,
    onChangeEditSelectedField: Function,
    onCancelEditSelectedField: Function,
    onSubmitEditSelectedField: Function,
    onUpload: Function,
    updateFieldBorderOperation: Function,
    onSubmitUploadOperations: Function,
    onCancelUploadOperations: Function,
    centerMapToFarm: Function,
    centerMapToField: Function,
  },
}

// Local Styles
const styles = {
  containerWrapper: ({ toggled }: Object): Object => ({
    position: 'absolute',
    top: 0,
    right: 'initial',
    left: 0,
    bottom: 0,
    width: 375,
    maxWidth: '100vw',
    zIndex: 401,
    transform: `translateX(${toggled ? 0 : -350}px)`,
    willChange: 'transform',
    transition: 'transform 0.1s ease-in-out',
    color: dark(),
  }),
  assetsPanel: {
    padding: '0 15px',
  },
  container: {
    backgroundColor: '#f3f3f3',
    position: 'absolute',
    top: 0,
    right: 'initial',
    left: 0,
    bottom: 0,
    width: 350,
    maxWidth: '100vw',
    zIndex: 401,
    color: dark(),
    overflowX: 'hidden',
    overflowY: 'auto',
  },
  featureItem: ({ isHovering, isSelected }: { isHovering: boolean, isSelected: boolean }): Object => ({
    display: 'flex',
    padding: 10,
    backgroundColor: isHovering ? dark(0.8) : isSelected ? primary() : dark(),
    color: white(),
    alignItems: 'center',
    cursor: 'pointer',
  }),
  featureItemSvg: {
    flex: '0 0 45px',
    height: 45,
    width: 45,
    marginRight: 15,
  },
  infoContainer: {
    flex: '1 1 auto',
  },
  iconButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  padding: {
    padding: 15,
  },
  label: {
    padding: 15,
  },
  togglePanelTab: ({ toggled }: Object): Object => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    width: 25,
    marginTop: -15,
    height: 30,
    backgroundColor: 'white',
    right: 0,
    top: '50%',
    borderTopRightRadius: '50%',
    borderBottomRightRadius: '50%',
    cursor: 'pointer',
    paddingRight: 2,
    transition: 'transform ease-in-out 0.2s',
    transform: `scale(${toggled ? 1 : 1.5})`,
    willChange: 'transform',
    boxShadow: '0 0 2px rgba(0, 0, 0, 0.2)',
  }),
  title: {
    fontWeight: 500,
  },
  area: {
    margin: '15px 0',
    fontSize: '0.875rem',
  },
  buttonWithMargin: {
    marginRight: 5,
    padding: '0 10px',
  },
  buttonNoMargin: {
    padding: '0 10px',
  },
  warningContainer: {
    paddingLeft: 15,
    paddingTop: 15,
  },
  warning: {
    color: orange(),
  },
  button: {
    padding: '0 8px',
    color: white(),
    marginBottom: 5,
  },
  buttonShort: {
    padding: '0 7px',
    color: white(),
    marginBottom: 5,
  },
  secondaryButton: {
    padding: '0 7px',
    color: white(),
    marginBottom: 5,
    backgroundColor: primary(),
  },
  enabledButton: {
    backgroundColor: primary(),
  },
}

// Exports
export class AssetsPanel extends PureComponent {
  props: Props

  constructor(props: Props) {
    super(props)

    this.onSubmitEditSelectedField = this.onSubmitEditSelectedField.bind(this)
    this.onCancelEditSelectedField = this.onCancelEditSelectedField.bind(this)
    this.onChangeLabel = this.onChangeLabel.bind(this)
    this.onDelete = this.onDelete.bind(this)
    this.renderLabel = this.renderLabel.bind(this)
    this.uploadBorders = this.uploadBorders.bind(this)
  }

  onCancelEditSelectedField() {
    this.props.actions.onCancelEditSelectedField(this.props.featureOperation)
  }

  onSubmitEditSelectedField() {
    this.props.actions.onSubmitEditSelectedField(this.props.featureOperation)
  }

  onChangeLabel(e: Event & { currentTarget: HTMLInputElement }) {
    this.props.actions.onChangeEditSelectedField({
      ...this.props.featureOperation,
      model: { [PROP_LABEL]: e.currentTarget.value },
    })
  }

  onDelete() {
    this.props.actions.onDeleteSelectedField({
      model: this.props.selectedFieldId,
    })
  }

  renderLabel(): React.Element<any> {
    const operation = this.props.featureOperation.operation
    const editLabel = (
      <Formfield style={styles.label}>
        <Textfield>
          <Textfield.Input
            placeholder={'Label'}
            value={this.props.featureOperation.model[PROP_LABEL]}
            onChange={this.onChangeLabel}
          />
        </Textfield>
      </Formfield>
    )
    const deleteConfirmationMsg = (
      <p style={styles.padding}>Are you sure you want to delete this field and all its spatial features?</p>
    )
    return operation === 'EDIT' ? editLabel : deleteConfirmationMsg
  }

  uploadBorders() {
    const { selectedFieldId, featureOperation } = this.props

    if (!featureOperation && selectedFieldId) {
      this.props.actions.onUpload({ selectedId: selectedFieldId, source: 'map' })
    }
  }

  render(): React.element<any> {
    const { isActiveToolsOp, isEditing: isEditingPolygon, featureOperation } = this.props
    const isEditing = featureOperation && featureOperation.operation === 'EDIT'
    const isDeleting = featureOperation && featureOperation.operation === 'DELETE'

    if (!_isEmpty(this.props.uploadOperations)) {
      const hasShape = _find(this.props.assets, { id: this.props.selectedFieldId }).shape
      return (
        <UploadFieldBorders
          displayWarning={hasShape}
          uploadOperations={this.props.uploadOperations}
          onChange={this.props.actions.updateFieldBorderOperation}
          onSubmit={this.props.actions.onSubmitUploadOperations}
          onCancel={this.props.actions.onCancelUploadOperations}
          assets={this.props.assets}
        />
      )
    } else {
      return (
        <div style={styles.assetsPanel}>
          {!isEditing && (
            <PanelAssetsForm
              key="_form"
              fields={this.props.fields}
              selectedSeasonId={this.props.selectedSeasonId}
              selectedFarmId={this.props.selectedFarmId}
              selectedFieldId={this.props.selectedFieldId}
              seasonSelectorOptions={this.props.seasonSelectorOptions}
              farmSelectorOptions={this.props.farmSelectorOptions}
              fieldSelectorOptions={this.props.fieldSelectorOptions}
              onChangeSeason={this.props.actions.onChangeSeason}
              onChangeFarm={this.props.actions.onChangeFarm}
              onChangeField={this.props.actions.onChangeField}
              centerMapToFarm={this.props.actions.centerMapToFarm}
              centerMapToField={this.props.actions.centerMapToField}
            />
          )}
          {!featureOperation && (
            <p style={styles.area}>
              <span style={styles.title}>Total Area: </span>
              {this.props.area}
            </p>
          )}
          {featureOperation && this.renderLabel()}
          <FeaturesOfField
            key="_features"
            selectedFieldId={this.props.selectedFieldId}
            features={this.props.featuresOfSelectedField}
            featureOperation={this.props.featureOperation}
            selectedFeatureIndex={this.props.selectedFeatureIndex}
            hoverFeatureIndex={this.props.hoverFeatureIndex}
            fitMapBounds={this.props.actions.fitMapBounds}
            onSelectFeature={this.props.actions.onSelectFeature}
            onToggleHoverFeature={this.props.actions.onToggleHoverFeature}
            onChange={this.props.actions.onChangeEditSelectedField}
          />
          <div style={styles.warningContainer} key="save-warning">
            {isEditingPolygon && (
              <span style={styles.warning}>
                You must first complete the field by clicking the "Finish" icon from the edit field toolbox.
              </span>
            )}
          </div>
          <div key="_buttons" style={{ marginTop: 15 }}>
            {!featureOperation && this.props.selectedFieldId
              ? [
                  <Button
                    key="edit-button"
                    disabled={this.props.jobsOfSelectedField.length > 0}
                    style={
                      this.props.jobsOfSelectedField.length > 0
                        ? { ...styles.buttonWithMargin, ...styles.buttonShort }
                        : { ...styles.buttonWithMargin, ...styles.buttonShort, ...styles.enabledButton }
                    }
                    raised
                    accent
                    onClick={this.props.actions.onEditSelectedField}
                  >
                    Edit Field
                    <Mask enabled={_size(this.props.uploadOperations)} />
                  </Button>,
                  <Button
                    key="upload-button"
                    disabled={this.props.jobsOfSelectedField.length > 0}
                    style={
                      this.props.jobsOfSelectedField.length > 0
                        ? { ...styles.buttonWithMargin, ...styles.buttonShort }
                        : { ...styles.buttonWithMargin, ...styles.buttonShort, ...styles.enabledButton }
                    }
                    raised
                    accent
                    onClick={this.uploadBorders}
                  >
                    Upload Border
                    <Mask enabled={_size(this.props.uploadOperations)} />
                  </Button>,
                  <Button
                    key="delete-button"
                    style={{ ...styles.buttonNoMargin, ...styles.secondaryButton }}
                    raised
                    onClick={this.onDelete}
                  >
                    Delete
                    <Mask enabled={_size(this.props.uploadOperations)} />
                  </Button>,
                ]
              : null}
            {featureOperation
              ? [
                  <Button
                    disabled={isEditingPolygon || isActiveToolsOp}
                    key="save-button"
                    raised
                    accent
                    style={
                      isEditingPolygon || isActiveToolsOp
                        ? { ...styles.buttonShort, marginRight: 5 }
                        : { ...styles.buttonShort, marginRight: 5, ...styles.enabledButton }
                    }
                    onClick={this.onSubmitEditSelectedField}
                  >
                    {isDeleting ? 'Confirm' : 'Save'}
                  </Button>,
                  <Button
                    key="cancel-button"
                    raised
                    disabled={isActiveToolsOp}
                    onClick={this.onCancelEditSelectedField}
                    style={styles.secondaryButton}
                  >
                    Cancel
                  </Button>,
                ]
              : null}
          </div>
        </div>
      )
    }
  }
}

export default AssetsPanel
